<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import TableRow from "@/components/global-themed/table/TableRow.vue";
import {computed, onMounted, ref} from "vue";
import _ from "lodash";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import axios from "axios";
import SweetToast from "@/SweetToast";
import {axiosCatchHandler} from "@/modules/sharedGlobal";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";

const props = defineProps({
  competitor: {
    type: Object,
    required: true,
  }
});

const orderBy = ref('position');
const orderDir = ref('asc');

const keywords = ref([]);
const loading = ref(false);

onMounted(() => {
  loading.value = true;
  axios.get(`/ajax/v2/competitors/${props.competitor.simpleUrl}/keywords`).then(({data}) => {
    keywords.value = data.data?.serps ?? [];
    loading.value = false;
  }).catch((e) => {
    axiosCatchHandler(e);
    SweetToast.fire({
      icon: 'error',
      title: 'Failed to load keywords',
    });
  })
})

const filteredKeywords = computed(() => {
  return _.orderBy(keywords.value, orderBy.value, orderDir.value);
});

const handleOrderChanged = (newOrderBy) => {
  if (newOrderBy === orderBy.value) {
    orderDir.value = orderDir.value === 'asc' ? 'desc' : 'asc';
    return;
  }

  orderBy.value = newOrderBy;
  orderDir.value = 'desc';
}

</script>

<template>
  <BaseModal>
    <div class="tw-flex tw-flex-col">
      <h2 class="tw-text-2xl tw-font-medium tw-antialiased">
        Matching Keywords for <span class="tw-text-blue">{{ competitor.simpleUrl }}</span>
      </h2>
      <DataTable>
        <TableHeaderRow>
          <TableHeaderCell
            col-span="10"
            :order-by="orderBy"
            :order-direction="orderDir"
            name="keyword"
            @order-changed="handleOrderChanged"
          >
            Keyword
          </TableHeaderCell>
          <TableHeaderCell
            col-span="2"
            :order-by="orderBy"
            :order-direction="orderDir"
            name="position"
            @order-changed="handleOrderChanged"
          >
            Position
          </TableHeaderCell>
        </TableHeaderRow>
        <TableBody class="tw-max-h-[40vh]">
          <TableRow
            v-for="keyword in filteredKeywords"
            :key="`${keyword.keyword}_${keyword.position}`"
          >
            <TableCell col-span="10">
              <p class="tw-font-medium">
                {{ keyword.keyword }}
              </p>
              <p>
                <a
                  :href="keyword.page"
                  target="_blank"
                  class="tw-text-blue tw-underline tw-max-w-full tw-truncate"
                >{{ keyword.page }}</a>
              </p>
            </TableCell>
            <TableCell
              col-span="2"
              class="tw-text-center"
            >
              <span class="tw-font-medium">{{ keyword.position }}</span>
            </TableCell>
          </TableRow>
        </TableBody>
        <ZeroContent v-if="!loading && keywords.length === 0">
          No matching keywords available
        </ZeroContent>
        <LoadingBlock v-if="loading" />
      </DataTable>
    </div>
  </BaseModal>
</template>